import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Flame, Clock } from "lucide-react";
import api from "@/lib/api";
import { Icon } from "@/lib/icons";
import ThreadCard from "@/components/ThreadCard";

const GOAL_LABELS = {
  build_strength: "Build Strength", lose_weight: "Lose Weight",
  improve_mobility: "Improve Mobility", feel_better: "Feel Better", start_again: "Start Again",
};

export default function Community() {
  const [params, setParams] = useSearchParams();
  const goal = params.get("goal");
  const [sort, setSort] = useState("trending");
  const [threads, setThreads] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/threads", { params: { sort, goal: goal || undefined, limit: 40 } })
      .then((r) => setThreads(r.data)).catch(() => {});
  }, [sort, goal]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-plum">Community</h1>
          <p className="mt-2 text-mutedplum text-lg">Honest conversations for women 45+. Ask anything—we've got you.</p>
        </div>
        <Link to="/new-discussion" data-testid="start-discussion-btn"
          className="inline-flex items-center gap-2 rounded-full bg-teal px-6 py-3 font-semibold text-white hover:bg-teal-dark transition-colors self-start">
          <Plus className="h-5 w-5" /> Start a discussion
        </Link>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        <aside className="lg:col-span-1 order-2 lg:order-1">
          <div className="bg-white rounded-3xl border border-rose/30 shadow-card p-3 sticky top-24">
            <h2 className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-mutedplum">Categories</h2>
            {cats.map((c) => (
              <Link key={c.slug} to={`/community/${c.slug}`} data-testid={`cat-side-${c.slug}`}
                className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-rose-soft transition-colors group">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-rose/30 text-teal shrink-0">
                  <Icon name={c.icon} className="h-4 w-4" />
                </span>
                <span className="flex-1 text-sm font-medium text-plum group-hover:text-teal transition-colors">{c.name}</span>
                <span className="text-xs text-mutedplum">{c.thread_count}</span>
              </Link>
            ))}
          </div>
        </aside>

        <div className="lg:col-span-3 order-1 lg:order-2">
          <h2 className="font-display text-2xl text-plum mb-4">Discussions</h2>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            {goal && (
              <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 text-teal px-4 py-1.5 text-sm font-semibold">
                Goal: {GOAL_LABELS[goal] || goal}
                <button onClick={() => setParams({})} className="hover:text-teal-dark" data-testid="clear-goal">✕</button>
              </span>
            )}
            <div className="flex gap-2 ml-auto">
              <button onClick={() => setSort("trending")} data-testid="sort-trending"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${sort === "trending" ? "bg-plum text-cream" : "bg-white border border-rose/50 text-plum-ink"}`}>
                <Flame className="h-4 w-4" /> Trending
              </button>
              <button onClick={() => setSort("new")} data-testid="sort-new"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${sort === "new" ? "bg-plum text-cream" : "bg-white border border-rose/50 text-plum-ink"}`}>
                <Clock className="h-4 w-4" /> Newest
              </button>
            </div>
          </div>

          {threads.length === 0 ? (
            <div className="bg-white rounded-3xl border border-rose/30 p-12 text-center text-mutedplum" data-testid="no-threads">
              No discussions here yet. <Link to="/new-discussion" className="text-teal font-semibold">Be the first to start one.</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="thread-list">
              {threads.map((t, i) => <ThreadCard key={t.thread_id} thread={t} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
